/**
 * Page publique d'annulation de réservation (audit B2).
 *
 * Ouverte depuis le lien de l'email de confirmation : `/annuler?id=..&token=..`.
 * Un client anonyme (sans compte) n'a AUCUN autre moyen d'annuler ; le jeton
 * opaque autorise l'annulation de cette résa uniquement. On demande une
 * confirmation explicite (pas d'annulation au simple chargement : un scanner de
 * liens d'email pourrait précharger l'URL).
 */
import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';

type State = 'confirm' | 'loading' | 'done' | 'error' | 'invalid';

export function AnnulerScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get('id');
  const token = params.get('token');

  const [state, setState] = useState<State>(id && token ? 'confirm' : 'invalid');

  const cancel = async () => {
    if (!id || !token) return;
    setState('loading');
    try {
      // PATCH anonyme : l'autorisation vient du jeton, pas d'un compte.
      const res = await apiFetch(`/reservations/${encodeURIComponent(id)}/cancel?token=${encodeURIComponent(token)}`, {
        method: 'PATCH',
        auth: false,
      });
      setState(res.ok ? 'done' : 'error');
    } catch {
      setState('error');
    }
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: '24px',
        textAlign: 'center',
        paddingTop: 'max(env(safe-area-inset-top, 0px), 24px)',
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)',
      }}
    >
      {state === 'invalid' && (
        <>
          <h1 style={{ color: 'var(--color-primary)' }}>Lien invalide</h1>
          <p style={{ color: '#666', maxWidth: 360 }}>
            Ce lien d'annulation est incomplet ou a expiré. Contactez directement le restaurant pour annuler.
          </p>
        </>
      )}

      {state === 'confirm' && (
        <>
          <h1 style={{ color: 'var(--color-primary)' }}>Annuler la réservation&nbsp;?</h1>
          <p style={{ color: '#666', maxWidth: 360 }}>
            Confirmez l'annulation de votre réservation. Cette action est définitive.
          </p>
          <button
            onClick={cancel}
            style={{
              background: 'var(--color-error, #b23b3b)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '14px 24px',
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            Oui, annuler ma réservation
          </button>
          <button
            onClick={() => navigate('/')}
            style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: 15 }}
          >
            Non, garder ma réservation
          </button>
        </>
      )}

      {state === 'loading' && <p style={{ color: '#666' }}>Annulation en cours…</p>}

      {state === 'done' && (
        <>
          <h1 style={{ color: 'var(--color-primary)' }}>Réservation annulée</h1>
          <p style={{ color: '#666', maxWidth: 360 }}>
            Votre réservation a bien été annulée. Un email de confirmation vous a été envoyé si une adresse était renseignée.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{ background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 12, padding: '14px 24px', fontSize: 16, fontWeight: 600 }}
          >
            Retour à l'accueil
          </button>
        </>
      )}

      {state === 'error' && (
        <>
          <h1 style={{ color: 'var(--color-error, #b23b3b)' }}>Annulation impossible</h1>
          <p style={{ color: '#666', maxWidth: 360 }}>
            Ce lien n'est plus valide, ou la réservation a déjà été annulée. Contactez le restaurant si besoin.
          </p>
          <button
            onClick={() => setState('confirm')}
            style={{ background: 'none', border: '1px solid var(--color-primary)', color: 'var(--color-primary)', borderRadius: 12, padding: '12px 20px', fontSize: 15 }}
          >
            Réessayer
          </button>
        </>
      )}
    </div>
  );
}
